import { useLocation, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { StateTabs } from '@components/header/state-tabs';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArchive } from '@components/icons/archive';
import { IconDate } from '@components/icons/date';
import { IconDownload } from '@components/icons/download';
import { IconDraft } from '@components/icons/draft';
import { IconPublish } from '@components/icons/publish';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';

import { Header, MobileActions } from 'components/headers';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { Jobs } from 'pages/Jobs';

// Maps each status tab to its persisted `chosenTab` index (see uiSettings.jobs).
const STATUS_INDEX = { published: 0, drafts: 1, expired: 2, archived: 3 };

const JobList = observer(() => {
  const { t, uiSettings } = useStore();
  const { jobs } = uiSettings;

  const tabs = [
    { id: 'published', label: t('jobs.tabs.published'), icon: <IconPublish /> },
    { id: 'expired', label: t('jobs.tabs.expired'), icon: <IconDate /> },
    { id: 'drafts', label: t('jobs.tabs.drafts'), icon: <IconDraft /> },
    { id: 'archived', label: t('jobs.tabs.archived'), icon: <IconArchive /> },
  ];

  return (
    <>
      <Header
        mobileTitle={t('navigation.jobs')}
        breadcrumbs={<Breadcrumb items={[{ label: t('navigation.jobs') }]} />}
        actions={<JobActions />}
        tabs={
          <StateTabs
            ariaLabel={t('jobs.tabs.label')}
            tabs={tabs}
            activeTab={jobs.tabText}
            onTabChange={(id) => jobs.changeTab(STATUS_INDEX[id])}
          />
        }
      />
      <Jobs />
    </>
  );
});

export default JobList;

const JobActions = observer(() => {
  const { uiSettings, t } = useStore();
  const { showModal } = useModal();
  const isMobile = useIsMobile();
  const settings = uiSettings.jobs;
  const { hasRolePermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const exportJobs = () => {
    showModal('EXPORT_JOBS');
  };

  if (isMobile) {
    return (
      <MobileActions>
        <SearchBox
          placeholder={t('jobs.search.placeholder')}
          value={settings.searchStr}
          onChange={settings.setSearchValue}
          isFullWidth
        />
      </MobileActions>
    );
  }

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch
        value={settings.searchStr}
        onChange={settings.setSearchValue}
        onChangeDebounce={0}
        placeholder={t('jobs.search.placeholder')}
      />
      <Button
        variant="secondary"
        text={t('jobs.export')}
        iconRight={<IconDownload />}
        onClick={exportJobs}
      />
      {hasRolePermission('job:create') && (
        <Button
          iconLeft={<IconAddPlus />}
          text={t('jobs.createjob')}
          onClick={() => navigate('/job/create', { state: { from: location.pathname } })}
          dataCy="navigate-job-create"
        />
      )}
    </Stack>
  );
});
